import { Component, Input, OnInit } from "@angular/core";
import { DriverNote } from "@app/shared/models/drivers/DriverViewModel.model";
import { DriverService } from "@app/driver/shared/driver.service";

@Component({
  selector: "app-client-driver-notes",
  templateUrl: "./client-driver-notes.component.html",
  styleUrls: ["./client-driver-notes.component.css"],
})
export class ClientDriverNotesComponent implements OnInit {
  @Input() client: any;

  notes: DriverNote[] = [];
  filteredNotes: DriverNote[] = [];
  searchText: string = "";
  filterStatus: string = "";
  filterType: string = "";
  filterDriver: string = "";
  filterDateFrom: string = "";
  filterDateTo: string = "";
  loading: boolean = false;
  pageSize: number = 10;

  // View modal
  showViewModal: boolean = false;
  selectedNote: Partial<DriverNote> = {};

  noteTypes: string[] = [
    "Driver Release",
    "Delays",
    "Breakdown & follow up",
    "Accident",
    "Incident",
  ];

  // Unique drivers for dropdown filter
  drivers: { id: string; name: string }[] = [];

  constructor(private driverService: DriverService) {}

  ngOnInit() {
    if (this.client?.id) {
      this.loadNotes();
    }
  }

  loadNotes() {
    this.loading = true;
    this.driverService.getClientDriverNotes(this.client.id).subscribe(
      (notes) => {
        this.notes = notes || [];
        // Build unique drivers list for filter dropdown
        const driverMap = new Map<string, string>();
        this.notes.forEach((note) => {
          if (note.driverId && note.driverName) {
            driverMap.set(note.driverId, note.driverName);
          }
        });
        this.drivers = Array.from(driverMap.entries())
          .map(([id, name]) => ({ id, name }))
          .sort((a, b) => a.name.localeCompare(b.name));

        this.filterNotes();
        this.loading = false;
      },
      (error) => {
        console.error("Error loading client driver notes:", error);
        this.notes = [];
        this.filterNotes();
        this.loading = false;
      }
    );
  }

  filterNotes() {
    this.filteredNotes = this.notes.filter((note) => {
      const searchLower = this.searchText?.toLowerCase() || "";
      const matchesSearch =
        !this.searchText ||
        (note.note || "").toLowerCase().includes(searchLower) ||
        (note.driverName || "").toLowerCase().includes(searchLower) ||
        (note.driverNo || "").toLowerCase().includes(searchLower) ||
        (note.citationDescription || "").toLowerCase().includes(searchLower) ||
        (note.tractorNumber || "").toLowerCase().includes(searchLower) ||
        (note.trailerNumber || "").toLowerCase().includes(searchLower) ||
        (note.loadedEmpty || "").toLowerCase().includes(searchLower) ||
        (note.productName || "").toLowerCase().includes(searchLower) ||
        (note.accidentCity || "").toLowerCase().includes(searchLower) ||
        (note.accidentState || "").toLowerCase().includes(searchLower) ||
        (note.accidentDescription || "").toLowerCase().includes(searchLower) ||
        (note.damageDescription || "").toLowerCase().includes(searchLower) ||
        (note.clientName || "").toLowerCase().includes(searchLower) ||
        (note.billToName || "").toLowerCase().includes(searchLower) ||
        (note.tripNumber || "").toLowerCase().includes(searchLower) ||
        (note.internalNotes || "").toLowerCase().includes(searchLower) ||
        (note.clientNotes || "").toLowerCase().includes(searchLower);
      const matchesStatus = !this.filterStatus || note.status === this.filterStatus;
      const matchesType = !this.filterType || note.type === this.filterType;
      const matchesDriver = !this.filterDriver || note.driverId === this.filterDriver;

      let matchesDate = true;
      if (this.filterDateFrom || this.filterDateTo) {
        const noteDate = note.dateCreatedUtc ? new Date(note.dateCreatedUtc) : null;
        if (noteDate) {
          const noteDateStr = noteDate.toLocaleDateString("en-CA");
          if (this.filterDateFrom && noteDateStr < this.filterDateFrom) {
            matchesDate = false;
          }
          if (this.filterDateTo && noteDateStr > this.filterDateTo) {
            matchesDate = false;
          }
        } else {
          matchesDate = false;
        }
      }

      return matchesSearch && matchesStatus && matchesType && matchesDriver && matchesDate;
    });

    // Sort by date created (newest first)
    this.filteredNotes.sort((a, b) => {
      const aDate = a.dateCreatedUtc ? new Date(a.dateCreatedUtc).getTime() : 0;
      const bDate = b.dateCreatedUtc ? new Date(b.dateCreatedUtc).getTime() : 0;
      return bDate - aDate;
    });
  }

  viewNote(note: DriverNote) {
    this.selectedNote = { ...note };
    this.showViewModal = true;
  }

  closeViewModal() {
    this.showViewModal = false;
    this.selectedNote = {};
  }
}
